import { FormEvent, useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { api, ApiError } from '../../lib/api'
import { getConfig, trackEvent } from '../../lib/config'
import { CheckboxField, Hcaptcha, HcaptchaHandle, isCaptchaRequired, SubmitButton } from '../../components/common'
import { AuthAlerts, AuthLayout, AuthLogo, LoginForm } from './AuthLayout'

declare global {
  interface Window {
    fbq?: (event: string, name: string) => void
  }
}

const INVALID_CREDENTIALS = 'Email o contraseña inválidos.'
const RATE_LIMITED = 'Demasiados intentos en poco tiempo. Espera unos minutos y vuelve a intentarlo.'
const UNAVAILABLE = 'No pudimos conectar con el servidor. Inténtalo nuevamente en unos momentos.'

type SignInFailure = {
  message: string
  reason: 'server_message' | 'rate_limited' | 'unavailable' | 'invalid_credentials'
  status_code?: number
  server_message?: string
}

function getSignInError(requestError: unknown): SignInFailure {
  if (!(requestError instanceof ApiError)) {
    return { message: UNAVAILABLE, reason: 'unavailable' }
  }
  const status_code = requestError.status
  const serverMessage = (requestError.body as { error?: string } | null)?.error
  if (serverMessage) {
    return { message: serverMessage, reason: 'server_message', status_code, server_message: serverMessage }
  }
  if (status_code === 429) {
    return { message: RATE_LIMITED, reason: 'rate_limited', status_code }
  }
  if (status_code >= 500) {
    return { message: UNAVAILABLE, reason: 'unavailable', status_code }
  }
  return { message: INVALID_CREDENTIALS, reason: 'invalid_credentials', status_code }
}

export default function SignInPage() {
  const config = getConfig()
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(true)
  const [error, setError] = useState<string | null>(config.flash.alert ?? null)
  const [notice, setNotice] = useState<string | null>(config.flash.notice ?? null)
  const [submitting, setSubmitting] = useState(false)
  const [captchaSolved, setCaptchaSolved] = useState(false)
  const captchaRef = useRef<HcaptchaHandle | null>(null)
  // A disabled default button also cancels Enter-to-submit from the password
  // field, which is how autofill got past the captcha.
  const canSubmit = !submitting && (captchaSolved || !isCaptchaRequired())

  useEffect(() => {
    if (searchParams.get('na') === 'true') {
      window.fbq?.('track', 'CompleteRegistration')
    }
  }, [searchParams])

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (submitting) {
      return
    }
    setSubmitting(true)
    setError(null)
    setNotice(null)
    trackEvent('Sign In Submitted')
    try {
      const result = await api.post<{ redirect: string }>('/professionals/sign_in', {
        professional: { email, password, remember_me: rememberMe ? '1' : '0' },
        'h-captcha-response': captchaRef.current?.getResponse() ?? '',
      })
      window.location.assign(result.redirect)
    } catch (requestError) {
      const { message, ...failure } = getSignInError(requestError)
      setError(message)
      trackEvent('Sign In Failed', failure)
      captchaRef.current?.reset()
      setSubmitting(false)
    }
  }

  return (
    <AuthLayout>
      <AuthAlerts notices={notice ? [notice] : []} errors={error ? [error] : []} />
      <LoginForm onSubmit={(event) => void handleSubmit(event)}>
          <AuthLogo />
          <div className="form-group col-xs-12">
            <input
              type="email"
              id="professional_email"
              name="professional[email]"
              className="form-control"
              placeholder="email"
              autoFocus
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <i className="fa fa-user" />
          </div>
          <div className="form-group col-xs-12">
            <input
              type="password"
              id="professional_password"
              name="professional[password]"
              className="form-control"
              placeholder="password"
              autoComplete="off"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <i className="fa fa-lock" />
          </div>
          <Hcaptcha theme="light" handleRef={captchaRef} form="sign_in" onResponseChange={setCaptchaSolved} />
          <div className="form-group col-xs-12">
            <CheckboxField
              id="professional_remember_me"
              name="professional[remember_me]"
              label="Recuérdame"
              checked={rememberMe}
              onChange={setRememberMe}
            />
          </div>
          <div className="form-group col-xs-12">
            <SubmitButton id="professional_submit" label="Ingresar" block style={{ width: '100%' }} disabled={!canSubmit} />
          </div>
          <section>
            <Link to="/professionals/password/new" style={{ margin: '0 auto' }}>
              ¿Ha olvidado tu contraseña?
            </Link>
            <br />
            <Link to="/professionals/confirmation/new">¿No ha recibido las instrucciones de confirmación?</Link>
            <br />
            <Link to="/professionals/sign_up">¿Quieres una nueva cuenta?</Link>
          </section>
      </LoginForm>
    </AuthLayout>
  )
}
